/**
 * Data layer for `prestashop-integration-via-webhook`. Thin typed fetch wrappers over /api/v1/prestashop-integration-via-webhook.
 * Raw `fetch()` is confined to this file per the dash contract.
 */

export interface PrestashopIntegrationViaWebhookRow {
	id: string;
	tenant_id: string;
	created_at: number;
	updated_at: number;
	[key: string]: unknown;
}

export interface ListPrestashopIntegrationViaWebhookResponse {
	count: number;
	items: PrestashopIntegrationViaWebhookRow[];
}

export interface CreatePrestashopIntegrationViaWebhookBody {
	[key: string]: unknown;
}

const BASE = "/api/v1/prestashop-integration-via-webhook";

async function parseOrThrow<T>(res: Response): Promise<T> {
	const text = await res.text();
	let data: unknown = null;
	if (text) {
		try {
			data = JSON.parse(text);
		} catch {
			data = text;
		}
	}
	if (!res.ok) {
		const msg =
			data &&
			typeof data === "object" &&
			data !== null &&
			"error" in data &&
			typeof (data as { error: unknown }).error === "string"
				? (data as { error: string }).error
				: `HTTP ${res.status}`;
		throw new Error(msg);
	}
	return data as T;
}

export async function list(): Promise<ListPrestashopIntegrationViaWebhookResponse> {
	const res = await fetch(BASE, {
		method: "GET",
		credentials: "include",
		headers: { accept: "application/json" },
	});
	return parseOrThrow<ListPrestashopIntegrationViaWebhookResponse>(res);
}

export async function create(body: CreatePrestashopIntegrationViaWebhookBody): Promise<PrestashopIntegrationViaWebhookRow> {
	const res = await fetch(BASE, {
		method: "POST",
		credentials: "include",
		headers: {
			accept: "application/json",
			"content-type": "application/json",
		},
		body: JSON.stringify(body ?? {}),
	});
	return parseOrThrow<PrestashopIntegrationViaWebhookRow>(res);
}

export async function remove(id: string): Promise<{ removed: boolean }> {
	const res = await fetch(`${BASE}/${encodeURIComponent(id)}`, {
		method: "DELETE",
		credentials: "include",
		headers: { accept: "application/json" },
	});
	return parseOrThrow<{ removed: boolean }>(res);
}

export const prestashopIntegrationViaWebhookQueryKeys = {
	all: ["prestashop-integration-via-webhook"] as const,
	list: () => [...prestashopIntegrationViaWebhookQueryKeys.all] as const,
	detail: (id: string) => [...prestashopIntegrationViaWebhookQueryKeys.all, id] as const,
};
